import React, { type ReactElement, type ReactNode } from "react";
import { cn } from "@ndd/ui";

// The eyebrow label (NO-791): a short label above or beside what it names, such
// as a rail section heading or a table header. Classic, it is the bold small
// caps the rail headings use today. Under the always theme it is mono caps:
// 11.5px, or 11px at size "sm" (the rail). It takes a className, so the theme
// reaches it through values only the theme defines (client/src/styles.css),
// each read with the classic value as its fallback; a caller's className still
// wins. A leaf module (`import React`, relative imports) so `tsx --test`
// renders it.

const EYEBROW_TYPE =
  "font-(family-name:--eyebrow-font,var(--font-sans)) font-(weight:--eyebrow-weight,700) uppercase tracking-(--eyebrow-tracking,0.08em) text-(color:--eyebrow-ink,var(--subtle))";

const EYEBROW_SIZE = {
  default: "text-(length:--eyebrow-size,var(--text-caption))",
  sm: "text-(length:--eyebrow-size-sm,var(--text-caption))",
} as const;

export type EyebrowSize = keyof typeof EYEBROW_SIZE;

/** The eyebrow's classes, for an element that can't be an Eyebrow. */
export function eyebrowClass(size: EyebrowSize = "default"): string {
  return cn(EYEBROW_TYPE, EYEBROW_SIZE[size]);
}

export function Eyebrow({
  as: Tag = "span",
  size = "default",
  className,
  children,
}: {
  as?: "span" | "p" | "h2" | "h3" | "dt" | "th";
  size?: EyebrowSize;
  className?: string;
  children: ReactNode;
}): ReactElement {
  return <Tag className={cn(eyebrowClass(size), className)}>{children}</Tag>;
}
